import { useState, useEffect } from "react";
import { startOfMonth, endOfMonth, format } from "date-fns";
import reqContaEntrada from "../../../services/reqMultiTenant/reqContaEntrada";
import reqBoletoConfig from "../../../services/reqMultiTenant/reqBoletoConfig";
import functions from "../../../util/functions";
import reqBoleto from "../../../services/reqCobranca/reqBoleto";
import { saveAs } from "file-saver";

export const PER_PAGE = 15;

export function semCadastro(item) {
	return Number(item.id_cliente) === 1;
}

/**
 * Estado e regras da tela de remessa.
 *
 * A selecao vive aqui, fora da lista de busca, para nao se perder
 * quando o usuario troca de pagina ou refaz o filtro.
 */
export function useRemessaBoleto() {
	const [activeTab, setActiveTab] = useState("buscar");

	// Parcelas escolhidas para entrar no arquivo.
	const [parcelas, setParcelas] = useState([]);

	// Convenio que vai gerar a remessa. Cada um tem sua propria faixa
	// de Nosso Numero, entao a escolha define de onde sai o numero.
	const [convenios, setConvenios] = useState([]);
	const [idBoletoConfig, setIdBoletoConfig] = useState(0);

	useEffect(() => {
		(async () => {
			try {
				const response = await reqBoletoConfig.listAll();
				const lista = Array.isArray(response?.data) ? response.data : [];
				setConvenios(lista);

				// Com um convenio so, nao faz o usuario escolher.
				if (lista.length === 1) setIdBoletoConfig(lista[0].id_boleto_config);
			} catch (e) {
				setErro("Nao foi possivel carregar os convenios de boleto.");
			}
		})();
	}, []);

	// Filtro
	const [dtIni, setDtIni] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
	const [dtFim, setDtFim] = useState(format(endOfMonth(new Date()), "yyyy-MM-dd"));
	const [nomeCliente, setNomeCliente] = useState("");
	const [idVenda, setIdVenda] = useState("");

	// Resultado da busca
	const [resultado, setResultado] = useState([]);
	const [pagina, setPagina] = useState(1);
	// Convencao do Portal para o backend que nao manda total: 0 significa
	// "pode ter mais", e a seta de avancar fica liberada. Ver o mesmo
	// calculo em components/product-search e pages/report/matrix.
	const [totalPaginas, setTotalPaginas] = useState(0);
	const [carregando, setCarregando] = useState(false);
	const [erro, setErro] = useState("");
	const [buscou, setBuscou] = useState(false);

	// Parcelas da pagina atual que ja possuem boleto. Mapa
	// id_conta_entrada -> { nosso_numero, numero_remessa }, para a
	// tela mostrar o motivo e em qual arquivo a parcela foi.
	const [jaEmitidos, setJaEmitidos] = useState({});

	// O objeto inteiro, nao so o id: a tela mostra agencia e conta
	// para o usuario conferir antes de gerar.
	const convenioSelecionado = convenios.find((c) => c.id_boleto_config === idBoletoConfig) || null;

	const totalValor = parcelas.reduce((soma, p) => soma + Number(p.valor || 0), 0);

	// Lookup O(1) para marcar as linhas ja escolhidas na busca.
	const idsSelecionados = new Set(parcelas.map((p) => p.id_conta_entrada));

	function jaSelecionada(id) {
		return idsSelecionados.has(id);
	}

	// Ordem da busca: as escolhidas no topo, as livres no meio e as que
	// ja tem boleto de outra remessa no fim - essas nao podem ser
	// clicadas, entao so atrapalham no meio das demais. O sort e estavel,
	// entao dentro de cada grupo a ordem que veio do servidor se mantem.
	function ordemNaBusca(item) {
		if (idsSelecionados.has(item.id_conta_entrada)) return 0;
		if (jaEmitidos[item.id_conta_entrada] || semCadastro(item)) return 2;

		return 1;
	}

	const resultadoOrdenado = [...resultado].sort(
		(a, b) => ordemNaBusca(a) - ordemNaBusca(b),
	);

	function adicionar(parcela) {
		// Guarda contra duplicata: registrar o mesmo titulo duas vezes
		// no banco e o erro mais caro dessa tela.
		if (jaSelecionada(parcela.id_conta_entrada)) return;
		setParcelas((atual) => [...atual, parcela]);
		setErro("");
	}

	function remover(idContaEntrada) {
		setParcelas((atual) => atual.filter((p) => p.id_conta_entrada !== idContaEntrada));
		setErro("");
	}

	function limparSelecao() {
		setParcelas([]);
		setErro("");
	}

	async function buscar(novaPagina = 1) {
		setCarregando(true);
		setErro("");
		try {
			// O backend so le dtini/dtfim quando paramdate e dtVenda ou
			// dtVencimento. page e perpage sao obrigatorios: o Delphi faz
			// StrToInt sem default e estoura se vierem vazios.
			const filtro = {
				paramdate: "dtVencimento",
				dtini: functions.dateByString(dtIni),
				dtfim: functions.dateByString(dtFim),
				active: "true",
				page: novaPagina,
				perpage: PER_PAGE,
			};

			// param/paramvalue e um par so no backend, entao os dois filtros
			// nao convivem: a venda tem precedencia por ser a mais especifica.
			// O codVenda vira StrToInt no Delphi - so digito chega aqui.
			if (idVenda.trim()) {
				filtro.param = "codVenda";
				filtro.paramvalue = idVenda.trim();
			} else if (nomeCliente.trim()) {
				filtro.param = "cliente";
				filtro.paramvalue = nomeCliente.trim();
			}

			const response = await reqContaEntrada.listPaginate(filtro);
			const lista = Array.isArray(response?.data) ? response.data : [];

			setResultado(lista);
			setPagina(novaPagina);
			setBuscou(true);
			// Pagina incompleta so acontece na ultima: trava o avanco aqui.
			setTotalPaginas(lista.length < PER_PAGE ? novaPagina : 0);

			// Pergunta so pelos ids desta pagina - 15 por vez, nao a
			// busca inteira. Bloquear na origem evita o usuario montar
			// a remessa e so descobrir o problema ao gerar.
			setJaEmitidos(await carregarJaEmitidos(lista));
		} catch (e) {
			setErro(e?.response?.data?.error || e?.message || "Falha ao buscar parcelas.");
			setResultado([]);
		} finally {
			setCarregando(false);
		}
	}

	async function carregarJaEmitidos(lista) {
		if (lista.length === 0) return {};

		try {
			const ids = lista.map((p) => p.id_conta_entrada);
			const response = await reqBoleto.jaEmitidos(ids);
			const emitidos = Array.isArray(response?.data) ? response.data : [];

			return emitidos.reduce((mapa, b) => {
				mapa[b.id_conta_entrada] = {
					nossoNumero: b.nosso_numero,
					numeroRemessa: b.numero_remessa,
				};
				return mapa;
			}, {});
		} catch (e) {
			// Falhar aqui nao pode impedir a busca: o servidor recusa
			// a remessa de qualquer forma se houver boleto repetido.
			return {};
		}
	}

	const [gerando, setGerando] = useState(false);

	// Resultado da ultima remessa. Enquanto existe, a tela mostra o
	// que foi gerado em vez da busca - o cliente precisa ver o que fez.
	const [resultadoRemessa, setResultadoRemessa] = useState(null);

	// So habilita com convenio escolhido e alguma parcela na lista.
	const podeGerar = idBoletoConfig > 0 && parcelas.length > 0 && !gerando;

	async function gerarRemessa() {
		if (!podeGerar) return;

		setGerando(true);
		setErro("");
		try {
			const ids = parcelas.map((p) => p.id_conta_entrada);
			const response = await reqBoleto.gerarRemessa(idBoletoConfig, ids);

			baixarArquivo(response.data);

			// Os boletos foram gravados no servidor: manter a selecao
			// faria o usuario gerar de novo e o servidor recusaria.
			setParcelas([]);
			setResultado([]);
			setBuscou(false);
			setResultadoRemessa(response.data);
		} catch (e) {
			// A rota devolve a recusa como texto puro - sem isto o usuario
			// so veria "Request failed with status code 500".
			setErro(
				typeof e?.response?.data === "string"
					? e.response.data
					: e?.response?.data?.error || e?.message || "Falha ao gerar a remessa.",
			);
		} finally {
			setGerando(false);
		}
	}

	function baixarNovamente() {
		if (resultadoRemessa) baixarArquivo(resultadoRemessa);
	}

	function novaRemessa() {
		setResultadoRemessa(null);
		setActiveTab("buscar");
	}

	// --- diagnostico: gera uma remessa com dados fixos no servidor ---
	const [testando, setTestando] = useState(false);

	async function baixarRemessaTeste() {
		setTestando(true);
		setErro("");
		try {
			const response = await reqBoleto.remessaTeste();
			saveAs(new Blob([response.data]), "remessa-teste.rem");
		} catch (e) {
			setErro(e.message);
		} finally {
			setTestando(false);
		}
	}

	return {
		activeTab,
		setActiveTab,

		convenios,
		convenioSelecionado,
		idBoletoConfig,
		setIdBoletoConfig,

		parcelas,
		totalValor,
		adicionar,
		remover,
		limparSelecao,
		jaSelecionada,

		dtIni,
		setDtIni,
		dtFim,
		setDtFim,
		nomeCliente,
		setNomeCliente,
		idVenda,
		setIdVenda,

		resultado: resultadoOrdenado,
		jaEmitidos,
		pagina,
		totalPaginas,
		carregando,
		erro,
		buscou,
		buscar,

		gerando,
		podeGerar,
		gerarRemessa,
		resultadoRemessa,
		baixarNovamente,
		novaRemessa,

		testando,
		baixarRemessaTeste,
	};
}

/* ---------- auxiliares ---------- */

// O servidor manda os arquivos em base64 dentro do JSON, para a resposta
// seguir o mesmo contrato das outras rotas. Aqui voltam a ser bytes.
export function bytesDeBase64(b64) {
	const binario = atob(b64 || "");
	const bytes = new Uint8Array(binario.length);
	for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);

	return bytes;
}

export function baixarArquivo(dados, nomePadrao = "remessa.rem") {
	saveAs(
		new Blob([bytesDeBase64(dados.arquivo)], { type: "text/plain" }),
		dados.nome_arquivo || nomePadrao,
	);
}
